import { Field, FieldValue, FieldValues, Schema, collectAllFields, same } from '../schema';

export interface FormState {
  values: FieldValues;
  initialValues: FieldValues;
  errors: Record<string, string>;
  // Staged membership of the layer this form writes, which the values alone cannot express: a
  // field holds the device type's default whether the layer stores it or not.
  //
  // Config keys rather than field ids, because one control can write several (`source_keys`), and
  // one of those keys can be another field's own -- tracking by key keeps the two agreeing.
  clearedKeys: string[];
  // Stored keys staged for removal. `pinnedKeys` is the other direction: unstored keys staged for
  // writing at whatever the field currently shows, which is how a value equal to the default gets
  // into the layer at all.
  pinnedKeys: string[];
}

export type FormAction =
  | { type: 'init'; values: FieldValues }
  // `pin` is set where the layer does not hold these keys yet: editing a field is how it asks to
  // join, which is the only way to store a value that happens to equal the type default.
  | { type: 'setValue'; id: string; value: FieldValue; keys?: string[]; pin?: boolean }
  | { type: 'setErrors'; errors: Record<string, string> }
  | { type: 'clearField'; id: string; keys: string[]; value: FieldValue }
  | { type: 'pinField'; keys: string[] }
  | { type: 'restoreField'; id: string; keys: string[] }
  | { type: 'reset' }
  | { type: 'commit'; values: FieldValues };

const without = (keys: string[], remove: string[]): string[] => keys.filter((k) => !remove.includes(k));

const union = (keys: string[], add: string[]): string[] => [...keys, ...add.filter((k) => !keys.includes(k))];

export const formReducer = (state: FormState, action: FormAction): FormState => {
  switch (action.type) {
    case 'init':
      return {
        values: { ...action.values },
        initialValues: { ...action.values },
        errors: {},
        clearedKeys: [],
        pinnedKeys: [],
      };
    case 'setValue':
      return {
        ...state,
        values: { ...state.values, [action.id]: action.value },
        // Typing into a field that was staged for clearing is a value the layer should keep, so
        // the clear goes. A pinned field stays pinned: editing it is still a field being set.
        clearedKeys: without(state.clearedKeys, action.keys ?? [action.id]),
        pinnedKeys: action.pin ? union(state.pinnedKeys, action.keys ?? [action.id]) : state.pinnedKeys,
      };
    case 'setErrors':
      return { ...state, errors: action.errors };
    case 'clearField':
      return {
        ...state,
        // Show what the field falls back to, so the consequence is on screen before saving.
        values: { ...state.values, [action.id]: action.value },
        clearedKeys: union(state.clearedKeys, action.keys),
        pinnedKeys: without(state.pinnedKeys, action.keys),
      };
    case 'pinField':
      return {
        ...state,
        pinnedKeys: union(state.pinnedKeys, action.keys),
        clearedKeys: without(state.clearedKeys, action.keys),
      };
    case 'restoreField':
      return {
        ...state,
        values: { ...state.values, [action.id]: state.initialValues[action.id] },
        clearedKeys: without(state.clearedKeys, action.keys),
        pinnedKeys: without(state.pinnedKeys, action.keys),
      };
    case 'reset':
      return {
        ...state,
        values: { ...state.initialValues },
        errors: {},
        clearedKeys: [],
        pinnedKeys: [],
      };
    case 'commit':
      return {
        values: { ...action.values },
        initialValues: { ...action.values },
        errors: {},
        clearedKeys: [],
        pinnedKeys: [],
      };
    default:
      return state;
  }
};

const defaultForType = (field: Field): FieldValue => {
  if (field.default !== undefined) return field.default as FieldValue;
  if (field.type === 'ToggleSwitch') return false;
  if (field.type === 'NumberField') return null;
  return '';
};

export const buildInitialValues = (schema: Schema, saved: FieldValues = {}): FieldValues => {
  const out: FieldValues = {};
  for (const field of collectAllFields(schema)) {
    out[field.id] = saved[field.id] !== undefined ? saved[field.id] : defaultForType(field);
  }
  return out;
};

export const dirtyKeys = (values: FieldValues, initial: FieldValues): string[] => {
  const keys = new Set([...Object.keys(values), ...Object.keys(initial)]);
  const out: string[] = [];
  for (const k of keys) {
    // `same`, not `!==`: a list or a perimeter is a fresh object every render, so a strict
    // comparison would report every one of them dirty and put it in the payload untouched.
    if (!same(values[k], initial[k])) out.push(k);
  }
  return out;
};
